import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeClass as _normalizeClass, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-col h-full overflow-hidden" }
const _hoisted_2 = {
  key: 0,
  class: "p-4 text-md border-0 border-b border-solid border-gray-200 flex gap-2"
}
const _hoisted_3 = { class: "p-4" }
const _hoisted_4 = { class: "text-red-400 text-sm px-4" }
const _hoisted_5 = { class: "flex flex-col h-full overflow-hidden" }
const _hoisted_6 = {
  class: "grid grid-cols-2 gap-4 justify-around p-4 relative min-h-300",
  "infinite-scroll-distance": 40
}
const _hoisted_7 = {
  key: 0,
  class: "absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center"
}
const _hoisted_8 = ["onClick", "disabled"]
const _hoisted_9 = { class: "h-8" }
const _hoisted_10 = {
  key: 2,
  class: "text-md"
}
const _hoisted_11 = { class: "flex flex-col gap-2" }
const _hoisted_12 = {
  key: 0,
  class: "pl-3 text-sm justify-center mt-2 flex w-full bg-blue-100 py-2"
}
const _hoisted_13 = { class: "mx-auto mb-4 relative" }

import {
	Dialog,
	UiButton,
	UiRadioTabs,
	SvgIcon,
	UiSelect,
	UiLoader,
	SelectOptions
} from '@/ui-kit';
import { useToggle } from '@vueuse/core';
import { useVirtualMailboxAddresses } from '../use-virtual-mailbox-addresses';
import { computed, ref, reactive } from 'vue';
import {
	useVirtualMailboxShipments,
	VirtualMailboxShipment
} from '../use-virtual-mailbox-shipments';
import { Mail } from '../mail';
import MailPreview from './MailPreview.vue';
import { inputEmits } from 'element-plus';
import { Mailbox } from '../mailbox';
import {
	CursorPaginationResponse,
	Nullable,
	httpService
} from '@/utils';
import { CancelTokenSource } from 'axios';
import { ElInfiniteScroll as vInfiniteScroll } from 'element-plus/lib';

type MailsPaginationState = {
	nextMailsLink: Nullable<string>;
	items: Mail[];
	loading: boolean;
	cancelToken: Nullable<CancelTokenSource>;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'AttachedMailsDialog',
  props: {
    mails: null,
    mailbox: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
	mails: Mail[];
	mailbox: Mailbox;
};

const [isOpened, setIsOpened] = useToggle();
const [loading, setLoading] = useToggle();
const [isItemsLoading, setIsItemsLoading] = useToggle();
const { loadAddresses, addressOptions, isLoading } =
	useVirtualMailboxAddresses();
const {
	createShipment,
	addShipmentPiece,
	removeShipmentPiece
} = useVirtualMailboxShipments();
const selectedAddress = ref(0);
const { toAddressString } = useVirtualMailboxAddresses();

const loadingIds = reactive<Set<number>>(new Set());
const error = ref('');

async function addPiece(mail: Mail) {
	if (!selectedShipment.value) {
		return;
	}
	loadingIds.add(mail.id);
	error.value = '';
	try {
		const shipment = await addShipmentPiece(
			props.mailbox.id,
			selectedShipment.value.id,
			mail.id
		);
		selectedShipment.value = shipment;
		unattachedMails.items =
			unattachedMails.items.filter(
				(item) => item.id !== mail.id
			);
		attachedMails.items = [
			...attachedMails.items,
			mail
		];
	} catch (e) {
		error.value = (e as any).response.data.message;
	} finally {
		loadingIds.delete(mail.id);
	}
}

async function removePiece(mail: Mail) {
	if (!selectedShipment.value) {
		return;
	}
	loadingIds.add(mail.id);
	error.value = '';
	try {
		const shipment = await removeShipmentPiece(
			props.mailbox.id,
			selectedShipment.value.id,
			mail.id
		);
		selectedShipment.value = shipment;
		attachedMails.items = attachedMails.items.filter(
			(item) => item.id !== mail.id
		);
		unattachedMails.items = [
			...unattachedMails.items,
			mail
		];
	} catch (e) {
		error.value = (e as any).response.data.message;
	} finally {
		loadingIds.delete(mail.id);
	}
}

function addOrRemovePiece(mail: Mail) {
	if (filteredTabs.value === 0) {
		return addPiece(mail);
	}

	removePiece(mail);
}



const selectedShipment = ref<VirtualMailboxShipment>();

expose({
	openDialog: async (
		shipment: VirtualMailboxShipment
	) => {
		error.value = '';
		selectedShipment.value = shipment;
		loadMails(shipment.id);
		setIsOpened(true);
	}
});

const filteredTabs = ref(0);

const tabOptions = ref<SelectOptions>([
	{
		title: 'Unattached mails',
		value: 0
	},
	{
		title: 'Attached mails',
		value: 1
	}
]);

async function loadMails(shipmentId: number) {
	await Promise.all([
		httpService
			.get<CursorPaginationResponse<any>>(
				`/company/virtual-mailbox/items/${props.mailbox.id}/shipment/${shipmentId}/pieces/attached`,
				attachedMails.cancelToken
					? {
							cancelToken:
								attachedMails.cancelToken
									.token
					  }
					: {}
			)
			.then((response) =>
				setResponseData(response, 'attached')
			),
		httpService
			.get<CursorPaginationResponse<any>>(
				`/company/virtual-mailbox/items/${props.mailbox.id}/shipment/${shipmentId}/pieces/unattached`,
				unattachedMails.cancelToken
					? {
							cancelToken:
								unattachedMails.cancelToken
									.token
					  }
					: {}
			)
			.then((response) =>
				setResponseData(response, 'unattached')
			)
	]);
}

function setResponseData(
	response: CursorPaginationResponse<any>,
	key: 'attached' | 'unattached'
) {
	if (key === 'attached') {
		attachedMails.items = [
			...attachedMails.items,
			...response.data
		];
		attachedMails.nextMailsLink = response.links.next;
		attachedMails.loading = false;
		attachedMails.cancelToken = null;
		return;
	}

	unattachedMails.items = [
		...unattachedMails.items,
		...response.data
	];
	unattachedMails.nextMailsLink = response.links.next;
	unattachedMails.cancelToken = null;
	unattachedMails.loading = false;
}

const attachedMails = reactive<MailsPaginationState>({
	nextMailsLink: null,
	items: [],
	loading: true,
	cancelToken: httpService.getCancelToken()
});

const unattachedMails = reactive<MailsPaginationState>({
	nextMailsLink: null,
	items: [],
	loading: true,
	cancelToken: httpService.getCancelToken()
});

function onClose() {
	if (attachedMails.cancelToken) {
		attachedMails.cancelToken.cancel();
	}

	if (unattachedMails.cancelToken) {
		unattachedMails.cancelToken.cancel();
	}
}

const mailsLoading = computed(
	() => attachedMails.loading && unattachedMails.loading
);

function cancelRequest($event: number) {
	if ($event === 0 && attachedMails.cancelToken) {
		attachedMails.cancelToken.cancel();
	}

	if ($event === 1 && unattachedMails.cancelToken) {
		unattachedMails.cancelToken.cancel();
	}
}

const currentState = computed(() => {
	if (filteredTabs.value === 0) {
		return {
			mails: unattachedMails.items,
			loading: unattachedMails.loading,
			nextMailsLink: unattachedMails.nextMailsLink
		};
	}

	return {
		mails: attachedMails.items,
		loading: attachedMails.loading,
		nextMailsLink: attachedMails.nextMailsLink
	};
});

function loadMore() {
	if (
		currentState.value.loading ||
		!currentState.value.nextMailsLink ||
		!selectedShipment.value
	) {
		return;
	}

	const cancelToken = httpService.getCancelToken();

	if (filteredTabs.value === 0) {
		unattachedMails.loading = true;
		unattachedMails.cancelToken = cancelToken;
	} else {
		attachedMails.loading = true;
		attachedMails.cancelToken = cancelToken;
	}

	const filter =
		filteredTabs.value === 0
			? 'unattached'
			: 'attached';

	httpService
		.get<CursorPaginationResponse<any>>(
			currentState.value.nextMailsLink,
			{
				cancelToken: cancelToken.token
			}
		)
		.then((response) =>
			setResponseData(response, filter)
		);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    preventDefaultClose: loadingIds.size > 0,
    modelValue: _unref(isOpened),
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => {
			_unref(setIsOpened)(false);
			onClose();
		})
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (selectedShipment.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("span", null, "Shipment " + _toDisplayString(selectedShipment.value?.id), 1),
              _createElementVNode("span", null, "to: " + _toDisplayString(_unref(toAddressString)(
							selectedShipment.value.address
						)), 1)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_unref(UiRadioTabs), {
            disabled: 
						loadingIds.size > 0 || _unref(mailsLoading)
					,
            noValidate: true,
            modelValue: filteredTabs.value,
            "onUpdate:modelValue": [
              _cache[0] || (_cache[0] = ($event: any) => ((filteredTabs).value = $event)),
              _cache[1] || (_cache[1] = ($event: any) => (
						cancelRequest($event)
					))
            ],
            options: tabOptions.value,
            name: "tab"
          }, null, 8, ["disabled", "modelValue", "options"])
        ]),
        _createElementVNode("div", _hoisted_4, _toDisplayString(error.value), 1),
        _createElementVNode("div", _hoisted_5, [
          _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_6, [
            (
							_unref(currentState).loading &&
							!_unref(currentState).mails.length
						)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createVNode(_unref(UiLoader))
                ]))
              : (
							_unref(currentState).mails.length
						)
                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(_unref(currentState).mails, (mail) => {
                    return (_openBlock(), _createElementBlock("button", {
                      class: "relative group cursor-pointer",
                      key: mail.id,
                      onClick: ($event: any) => (addOrRemovePiece(mail)),
                      disabled: 
								loadingIds.has(mail.id)
							
                    }, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["flex flex-col z-10 group-hover:opacity-100 opacity-0 transition duration-100 text-white items-center justify-center absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 gap-4", {
									'opacity-100':
										loadingIds.has(
											mail.id
										)
								}])
                      }, [
                        _createElementVNode("span", null, _toDisplayString(filteredTabs.value === 0
										? 'Attach mail'
										: 'Detach mail'), 1),
                        _createElementVNode("div", _hoisted_9, [
                          (
											!loadingIds.has(
												mail.id
											)
										)
                            ? (_openBlock(), _createBlock(_unref(SvgIcon), {
                                key: 0,
                                name: 
											filteredTabs.value ===
											0
												? 'only-check'
												: 'only-close'
										
                              }, null, 8, ["name"]))
                            : (_openBlock(), _createBlock(_unref(UiLoader), { key: 1 }))
                        ])
                      ], 2),
                      _createVNode(MailPreview, {
                        mail: mail,
                        selectedMailbox: __props.mailbox
                      }, null, 8, ["mail", "selectedMailbox"])
                    ], 8, _hoisted_8))
                  }), 128))
                : (_openBlock(), _createElementBlock("span", _hoisted_10, " No " + _toDisplayString(filteredTabs.value === 0
									? 'Unattached'
									: 'Attached') + " mails ", 1))
          ])), [
            [_unref(vInfiniteScroll), loadMore]
          ])
        ]),
        _createElementVNode("div", _hoisted_11, [
          (
						_unref(currentState).loading &&
						_unref(currentState).mails.length
					)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createTextVNode(" Loading more messages "),
                _createVNode(_unref(UiLoader), { class: "ml-2" })
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_13, [
            _createVNode(_unref(UiButton), {
              onClick: _cache[2] || (_cache[2] = ($event: any) => {
							_unref(setIsOpened)(false);
							onClose();
						}),
              theme: "primary-outlined",
              type: "button",
              class: "!text-sm !h-10 !min-w-20 w-32 !px-0",
              disabled: _unref(loading)
            }, {
              default: _withCtx(() => [
                _createTextVNode(" Close ")
              ]),
              _: 1
            }, 8, ["disabled"])
          ])
        ])
      ])
    ]),
    _: 1
  }, 8, ["preventDefaultClose", "modelValue"]))
}
}

})